import { useFormContext } from 'react-hook-form';
import { Box, Paper } from '@mui/material';
import { VariableSizeList as List, ListChildComponentProps } from 'react-window';
import { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks';
import { setSetupLanguageChanges } from '@store/reducers/project.reducer';
import { TranslationSetupForm, TranslationContentList } from '@customTypes';
import { EMPTY_STRING, ZERO, FIVE } from '@constants';

import styles from './TranslationSetupDialog.module.scss';
import TranslationItem from './TranslationItem';
import { getFormFieldKey, getItemHeightByTextLength } from './TranslationSetupDialog.helpers';

interface Props {
  data: TranslationContentList | undefined;
  activeTab: number;
}

export const TranslationSetup: FC<Props> = ({ data, activeTab }) => {
  const [containerHeight, setContainerHeight] = useState(750);
  const { control, setValue, getValues } = useFormContext<TranslationSetupForm>();
  const dispatch = useAppDispatch();
  const {
    setupLanguageDialog: { changedValues },
  } = useAppSelector((state) => state.projects);
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (containerRef.current) {
        setContainerHeight(containerRef.current.clientHeight - FIVE);
      }
    });
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  const getChangedValues = useCallback(() => {
    const changed: Record<string, { oldValue: string; newValue: string }> = {};
    data?.content.forEach((el) => {
      const currentValue = getValues(getFormFieldKey(el.textKey, activeTab)) as string;
      if (currentValue && currentValue !== el.translatedText) {
        changed[el.textKey] = {
          oldValue: el.translatedText,
          newValue: currentValue,
        };
      }
    });

    return changed;
  }, [data, getValues, activeTab]);

  const saveTranslation = useCallback(
    (textKey: string) => {
      const changed = getChangedValues();
      const currentValue = getValues(getFormFieldKey(textKey, activeTab));

      if (Object.keys(changed).length) {
        dispatch(setSetupLanguageChanges(changed));
      }

      setValue(getFormFieldKey(textKey, activeTab), currentValue);
    },
    [getValues, getChangedValues, setValue, activeTab],
  );

  useEffect(() => {
    data?.content.forEach((item) => {
      setValue(getFormFieldKey(item.textKey, activeTab), item.translatedText || EMPTY_STRING, {
        shouldDirty: true,
        shouldValidate: false,
      });
    });
  }, [data, setValue]);

  const renderedItems = useMemo(() => {
    if (!data) return null;
    const TranslationRow = ({ index, style }: ListChildComponentProps) => {
      const item = data.content[index];

      return (
        <div style={style} key={item.textKey}>
          <TranslationItem
            index={index}
            item={item}
            fromLanguage={data.fromLanguage.name}
            toLanguage={data.toLanguage.name}
            isChanged={!!changedValues[item.textKey]}
            onSave={saveTranslation}
            activeTab={activeTab}
            getChangedValues={getChangedValues}
          />
        </div>
      );
    };

    return (
      <List
        height={containerHeight}
        itemCount={data.content.length}
        itemSize={(index) => getItemHeightByTextLength(data?.content, index)}
        width="100%"
      >
        {TranslationRow}
      </List>
    );
  }, [data, control, saveTranslation, changedValues, getItemHeightByTextLength, containerHeight, activeTab]);

  return (
    <Box ref={containerRef} className={styles.tabsContentBox}>
      <Paper elevation={ZERO} className={styles.formContainer}>
        {renderedItems}
      </Paper>
    </Box>
  );
};
